import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/Brand";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/crm";

export default function TaskCard({ t, onChanged, showStudent }) {
  const [note, setNote] = useState(t.review_note || "");
  const [busy, setBusy] = useState(false);
  const ev = t.evidence || { checks: [], done: 0, total: 0 };
  const pct = ev.total ? Math.round((ev.done / ev.total) * 100) : 0;

  const review = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/tasks/${t.id}/review`, { status, note });
      toast.success(status === "Open" ? "Task reopened" : `Marked ${status}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${t.title}" for this associate?`)) return;
    try {
      await api.delete(`/admin/tasks/${t.id}`);
      toast.success("Task removed");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="rounded-2xl border bg-white p-5 space-y-4" data-testid={`task-card-${t.key}-${t.student_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {showStudent && <div className="text-[10px] uppercase tracking-[0.2em] text-[#C59B27] font-bold">{t.student_name}</div>}
          <div className="font-display text-lg font-bold text-[#19082B]">{t.title}</div>
          <div className="text-xs text-stone-500">Assigned {fmtDateTime(t.created_at)}{t.submitted_at && ` · Submitted ${fmtDateTime(t.submitted_at)}`}</div>
        </div>
        <StatusBadge status={t.status} testId={`task-status-${t.key}-${t.student_id}`} />
      </div>

      {ev.total > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-stone-500"><span>Expected outcome</span><span data-testid={`task-progress-${t.key}-${t.student_id}`}>{ev.done}/{ev.total} · {pct}%</span></div>
          <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-[#D4AF37] transition-[width]" style={{ width: `${pct}%` }} /></div>
          <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">
            {ev.checks.map((c) => (
              <li key={c.label} className={`flex items-start gap-2 ${c.ok ? "text-emerald-800" : "text-stone-600"}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" /> : <Circle className="h-4 w-4 mt-0.5 shrink-0 text-stone-300" />}{c.label}
              </li>
            ))}
          </ul>
        </div>
      )}
      {t.key === "custom" && <p className="text-sm text-stone-600 whitespace-pre-wrap">{t.body}</p>}
      {t.student_note && <div className="rounded-lg bg-[#F6F2FB] p-3 text-sm"><span className="font-semibold text-[#3B185F]">Associate's note: </span>{t.student_note}</div>}

      <div className="space-y-2 border-t pt-4">
        <Textarea rows={2} placeholder="Review note (shown to the associate)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`task-review-note-${t.key}-${t.student_id}`} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy || t.status === "Completed"} onClick={() => review("Completed")} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`task-complete-${t.key}-${t.student_id}`}>Mark Completed</Button>
          <Button size="sm" disabled={busy || t.status === "Needs Correction"} variant="outline" className="text-red-700 border-red-200" onClick={() => review("Needs Correction")} data-testid={`task-correction-${t.key}-${t.student_id}`}>Needs Correction</Button>
          {t.status !== "Open" && <Button size="sm" disabled={busy} variant="ghost" onClick={() => review("Open")} data-testid={`task-reopen-${t.key}-${t.student_id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen</Button>}
          <Button size="sm" variant="ghost" className="ml-auto text-stone-400 hover:text-red-600" onClick={remove} data-testid={`task-remove-${t.key}-${t.student_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
